import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import useSeo from "@/components/useSeo";
import { motion } from "framer-motion";
import {
  CalendarCheck, LineChart, CreditCard, Sparkles, Trophy, MessagesSquare,
  UsersRound, ClipboardList, Check, ArrowRight, ShieldCheck,
} from "lucide-react";

const SPORTS = ["Soccer","Basketball","Baseball","Cricket","Volleyball","Tennis","Hockey","Rugby","American Football","Wrestling","Martial Arts","Swimming","Track & Field","Gymnastics","Softball","Handball"];

const FEATURES = [
  { icon: UsersRound, title: "Unlimited Teams", desc: "U8 to Varsity, boys, girls, adult — organize every roster with coaches, assistants & managers." },
  { icon: CalendarCheck, title: "One-Tap Attendance", desc: "Present, Late, Excused, Absent. Auto-calculated attendance percentages per athlete." },
  { icon: LineChart, title: "Performance Ratings", desc: "Rate skill, speed, discipline, leadership & more. Weekly and monthly progress reports." },
  { icon: ClipboardList, title: "Training Planner", desc: "Build warm-ups, drills, scrimmages & conditioning. Reuse plans week after week." },
  { icon: CreditCard, title: "Collect Payments", desc: "Tuition, uniforms, tournaments & camps under your own club branding." },
  { icon: Sparkles, title: "AI Coach Assistant", desc: "AI practice planner, player evaluations, season reports & skill improvement plans." },
];

const PLANS = [
  { name: "Free", price: "$0", tag: "Get started", features: ["Up to 15 athletes", "1 team", "Attendance & ratings", "Basic dashboard"] },
  { name: "Starter", price: "$9.99", tag: "Growing clubs", features: ["Up to 50 athletes", "Unlimited teams", "Training planner", "Match tracking"] },
  { name: "Pro", price: "$24.99", featured: true, tag: "Most popular", features: ["Unlimited athletes", "Collect payments", "AI features", "Advanced reports"] },
  { name: "Club", price: "$49.99", tag: "Multi-team orgs", features: ["Everything in Pro", "Multiple teams & coaches", "Custom branding", "Priority support"] },
];

export default function Landing() {
  useSeo({
    title: "SphereHyx — Sports Club Management Software for Coaches & Clubs",
    description: "Run your entire sports club from one command center — teams, athletes, attendance, performance, payments, subscriptions and inter-club tournaments with live scoring. Free forever plan.",
    keywords: "sports club management software, team management app, athlete management, attendance tracking, sports club payments, tournament management, coach app, youth sports",
    canonical: "https://spherehyx.com/",
  });
  return (
    <div className="min-h-screen bg-background text-foreground grain overflow-x-hidden">
      <header className="fixed top-0 inset-x-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" className="text-muted-foreground hover:text-foreground"><Link to="/blog" data-testid="nav-blog-link">Blog</Link></Button>
            <Button asChild variant="ghost" className="text-muted-foreground hover:text-foreground" data-testid="nav-login-btn">
              <Link to="/login">Log in</Link>
            </Button>
            <Button asChild className="brand-gradient text-white border-0 rounded-full px-5 hover:opacity-90" data-testid="nav-signup-btn">
              <Link to="/register">Start free <ArrowRight size={16} className="ml-1" /></Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-40 pb-28 px-6">
        <div className="absolute inset-0 -z-10">
          <img src="https://images.unsplash.com/photo-1513635625218-6956bc843133?auto=format&fit=crop&w=1600&q=80" alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
        </div>
        <div className="max-w-5xl mx-auto">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="uppercase tracking-[0.25em] text-xs text-secondary font-semibold mb-5">
            The sports club operating system
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
            Run your entire club from <span className="brand-gradient-text">one command center.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-6 text-lg text-muted-foreground max-w-2xl">
            SphereHyx gives every coach a branded club to manage teams, attendance, performance,
            payments and parents — across 16 sports, with AI built in.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-9 flex flex-wrap gap-3">
            <Button asChild size="lg" className="brand-gradient text-white border-0 rounded-full px-7 h-12 text-base hover:opacity-90" data-testid="hero-cta-btn">
              <Link to="/register">Create your club free <ArrowRight size={18} className="ml-1.5" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-7 h-12 text-base" data-testid="hero-pricing-btn">
              <a href="#pricing">See pricing</a>
            </Button>
          </motion.div>
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck size={16} className="text-secondary" /> No credit card required · Free forever plan
          </div>
        </div>
      </section>

      {/* Sports marquee */}
      <section className="border-y border-border py-6 bg-card/30">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap gap-x-6 gap-y-2 justify-center">
          {SPORTS.map((s) => (
            <span key={s} className="text-sm text-muted-foreground font-medium">{s}</span>
          ))}
        </div>
      </section>

      {/* Features bento */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight max-w-xl">Everything a coach needs. Nothing they don't.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
                className="rounded-2xl border border-border bg-card p-7 hover:-translate-y-1 transition-transform duration-200">
                <div className="h-11 w-11 rounded-xl brand-gradient flex items-center justify-center mb-5">
                  <f.icon size={20} className="text-white" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6 bg-card/20 border-y border-border">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <p className="uppercase tracking-[0.25em] text-xs text-secondary font-semibold mb-3">Pricing</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Plans that grow with your club</h2>
            <p className="text-muted-foreground mt-3">We charge coaches, never players. Upgrade any time.</p>
            <div className="inline-flex items-center gap-2 mt-6 rounded-full brand-gradient text-white px-4 py-2 text-sm font-semibold" data-testid="promo-banner">
              <Sparkles size={15} /> Limited time: 50% off your first month on any paid plan
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {PLANS.map((p) => {
              const monthly = Number(p.price.replace("$", ""));
              const paid = monthly > 0;
              const firstMonth = paid ? (monthly / 2).toFixed(2) : null;
              return (
              <div key={p.name} data-testid={`plan-card-${p.name.toLowerCase()}`}
                className={`rounded-2xl border p-7 flex flex-col ${p.featured ? "border-primary bg-primary/5 relative" : "border-border bg-card"}`}>
                {p.featured && <span className="absolute -top-3 left-7 text-xs font-semibold brand-gradient text-white px-3 py-1 rounded-full">Most popular</span>}
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">{p.tag}</p>
                  {paid && <span className="text-[10px] font-bold uppercase tracking-wide bg-secondary/15 text-secondary px-2 py-0.5 rounded-full shrink-0" data-testid={`promo-badge-${p.name.toLowerCase()}`}>50% off 1st mo</span>}
                </div>
                <h3 className="font-heading font-bold text-2xl mt-2">{p.name}</h3>
                <p className="mt-3"><span className="font-heading font-extrabold text-4xl">{p.price}</span><span className="text-muted-foreground text-sm">/mo</span></p>
                {paid && <p className="text-sm text-secondary font-medium mt-1" data-testid={`first-month-${p.name.toLowerCase()}`}>First month just ${firstMonth}</p>}
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="text-secondary mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button asChild className={`mt-7 rounded-full ${p.featured ? "brand-gradient text-white border-0 hover:opacity-90" : ""}`} variant={p.featured ? "default" : "outline"}>
                  <Link to="/register">Get started</Link>
                </Button>
              </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-8">Pro & Club plans also enable online fee collection — SphereHyx charges no platform fee, only Stripe's processing fee applies (covered by the payer).</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Trophy size={40} className="mx-auto text-secondary mb-6" />
          <h2 className="font-heading font-extrabold text-3xl sm:text-5xl tracking-tight">Your club deserves a platform, not a spreadsheet.</h2>
          <Button asChild size="lg" className="mt-9 brand-gradient text-white border-0 rounded-full px-8 h-12 text-base hover:opacity-90" data-testid="footer-cta-btn">
            <Link to="/register">Start free today <ArrowRight size={18} className="ml-1.5" /></Link>
          </Button>
        </div>
      </section>

      <footer className="border-t border-border py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo size={28} />
          <div className="flex items-center gap-5 text-sm text-muted-foreground">
            <a href="/privacy" className="hover:text-foreground transition-colors" data-testid="footer-privacy-link">Privacy Policy</a>
            <a href="/terms" className="hover:text-foreground transition-colors" data-testid="footer-terms-link">Terms &amp; Conditions</a>
            <a href="/blog" className="hover:text-foreground transition-colors" data-testid="footer-blog-link">Blog</a>
          </div>
          <p className="text-sm text-muted-foreground">© 2026 SphereHyx. Connect · Manage · Grow.</p>
        </div>
      </footer>
    </div>
  );
}
